import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { Cookie, Settings, X } from "lucide-react";
import { Link } from "react-router-dom";
import {
  type CookiePreferences,
  hasConsentBeenGiven,
  getCookiePreferences,
  savePreferences,
} from "@/hooks/useCookieConsent";

const CookieConsent = () => {
  const [isVisible, setIsVisible] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const [preferences, setPreferences] = useState<CookiePreferences>({
    necessary: true,
    analytics: false,
    marketing: false,
  });

  useEffect(() => {
    if (!hasConsentBeenGiven()) {
      const timer = setTimeout(() => setIsVisible(true), 1000);
      return () => clearTimeout(timer);
    }
  }, []);

  const handleAcceptAll = () => {
    const allAccepted: CookiePreferences = { necessary: true, analytics: true, marketing: true };
    setPreferences(allAccepted);
    savePreferences(allAccepted);
    setIsVisible(false);
  };

  const handleAcceptNecessary = () => {
    const onlyNecessary: CookiePreferences = { necessary: true, analytics: false, marketing: false };
    setPreferences(onlyNecessary);
    savePreferences(onlyNecessary);
    setIsVisible(false);
  };

  const handleSavePreferences = () => {
    savePreferences(preferences);
    setIsVisible(false);
  };

  if (!isVisible) return null;

  return (
    <div className="fixed bottom-0 left-0 right-0 z-50 p-4 animate-in slide-in-from-bottom-5 duration-500">
      <Card className="max-w-4xl mx-auto p-6 shadow-2xl border-border bg-card/95 backdrop-blur-md">
        {!showSettings ? (
          // Main consent view
          <div className="space-y-4">
            <div className="flex items-start gap-4">
              <div className="p-3 rounded-full bg-primary/10 shrink-0">
                <Cookie className="w-6 h-6 text-primary" />
              </div>
              <div className="flex-1">
                <h3 className="font-semibold text-lg mb-1">
                  Utilizamos cookies 🍪
                </h3>
                <p className="text-muted-foreground text-sm">
                  Usamos cookies para melhorar sua experiência, analisar o tráfego do site e personalizar conteúdo. 
                  Ao clicar em "Aceitar todos", você concorda com o uso de todos os cookies. 
                  Você pode gerenciar suas preferências a qualquer momento.{" "}
                  <Link to="/cookies" className="text-primary hover:underline">
                    Política de Cookies
                  </Link>{" · "}
                  <Link to="/privacidade" className="text-primary hover:underline">
                    Privacidade
                  </Link>
                </p>
              </div>
            </div>

            <div className="flex flex-wrap items-center gap-3 justify-end pt-2">
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setShowSettings(true)}
                className="gap-2"
              >
                <Settings className="w-4 h-4" />
                Configurar
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={handleAcceptNecessary}
              >
                Apenas necessários
              </Button>
              <Button
                size="sm"
                onClick={handleAcceptAll}
                className="bg-gradient-primary hover:opacity-90"
              >
                Aceitar todos
              </Button>
            </div>
          </div>
        ) : (
          // Settings view
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="p-2 rounded-full bg-primary/10">
                  <Settings className="w-5 h-5 text-primary" />
                </div>
                <h3 className="font-semibold text-lg">Configurar cookies</h3>
              </div>
              <Button
                variant="ghost"
                size="icon"
                onClick={() => setShowSettings(false)}
              >
                <X className="w-4 h-4" />
              </Button>
            </div>

            <div className="space-y-4 py-2">
              {/* Necessary cookies */}
              <div className="flex items-center justify-between p-3 rounded-lg bg-muted/50">
                <div className="space-y-0.5">
                  <Label className="font-medium">Cookies necessários</Label>
                  <p className="text-xs text-muted-foreground">
                    Essenciais para o funcionamento do site. Não podem ser desativados.
                  </p>
                </div>
                <Switch checked disabled />
              </div>

              {/* Analytics cookies */}
              <div className="flex items-center justify-between p-3 rounded-lg bg-muted/50">
                <div className="space-y-0.5">
                  <Label className="font-medium">Cookies de análise</Label>
                  <p className="text-xs text-muted-foreground">
                    Nos ajudam a entender como você usa o site para melhorar sua experiência.
                  </p>
                </div>
                <Switch
                  checked={preferences.analytics}
                  onCheckedChange={(checked) =>
                    setPreferences((prev) => ({ ...prev, analytics: checked }))
                  }
                />
              </div>

              {/* Marketing cookies */}
              <div className="flex items-center justify-between p-3 rounded-lg bg-muted/50">
                <div className="space-y-0.5">
                  <Label className="font-medium">Cookies de marketing</Label>
                  <p className="text-xs text-muted-foreground">
                    Usados para exibir anúncios relevantes e campanhas personalizadas.
                  </p>
                </div>
                <Switch
                  checked={preferences.marketing}
                  onCheckedChange={(checked) =>
                    setPreferences((prev) => ({ ...prev, marketing: checked }))
                  }
                />
              </div>
            </div>

            <div className="flex items-center gap-3 justify-end pt-2 border-t">
              <Button
                variant="outline"
                size="sm"
                onClick={() => setShowSettings(false)}
              >
                Voltar
              </Button>
              <Button
                size="sm"
                onClick={handleSavePreferences}
                className="bg-gradient-primary hover:opacity-90"
              >
                Salvar preferências
              </Button>
            </div>
          </div>
        )}
      </Card>
    </div>
  );
};

export default CookieConsent;
